import {
  Banner,
  Box,
  Button,
  Flex,
  InfoIcon,
  Typography,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import { useState } from 'react';

import { initiateCrmAuth } from '@jane/monolith/sources/crmIntegration';
/* eslint-disable-next-line no-restricted-imports */
import type {
  CrmDetails,
  CrmIntegration,
  CrmReward,
} from '@jane/shared/models';

import { ApplyableDiscount } from './applyableDiscount';
import { StoreRedeemableListItem } from './customerLoyaltyPoints.styles';
import { UnlockWalletForm } from './unlockWalletForm/unlockWalletForm';

export const CustomerLoyaltyPointsContent = ({
  cartUuid,
  crmIntegration: { signed_up, wallet_state, automatic_redemption },
  error,
  handleApplyDiscount,
  loyaltyPointsStacking = true,
  maxRedemptionCountExceeded,
  noRewards,
  onUnlockWalletSubmit,
  crmDetails,
  redeemableInStore,
  redeemableOnline,
  reservationMode,
  storeLoyaltyPointsLabel,
  storeName,
  unlockLoading,
}: {
  cartUuid: string;
  crmDetails: CrmDetails;
  crmIntegration: CrmIntegration;
  error?: string;
  handleApplyDiscount: (reward: CrmReward) => void;
  loyaltyPointsStacking: boolean;
  maxRedemptionCountExceeded: boolean;
  noRewards: boolean;
  onUnlockWalletSubmit: (code: string) => void;
  redeemableInStore: CrmReward[];
  redeemableOnline: CrmReward[];
  reservationMode: string;
  storeLoyaltyPointsLabel?: string | null;
  storeName: string;
  unlockLoading: boolean;
}) => {
  const [acknowledgedStacking, setAcknowledgedStacking] = useState(false);

  if (error) {
    return (
      <Typography pl={40}>Unable to get your store reward points</Typography>
    );
  }

  if (!signed_up) {
    return (
      <Typography pl={40}>{`Ask about becoming a ${
        storeLoyaltyPointsLabel ? storeLoyaltyPointsLabel : 'loyalty points'
      } member at ${storeName.trim()}!`}</Typography>
    );
  }

  if (signed_up && wallet_state === 'locked') {
    return (
      <UnlockWalletForm
        onSubmit={onUnlockWalletSubmit}
        crmDetails={crmDetails}
        onSendCode={() => initiateCrmAuth(cartUuid)}
        unlockLoading={unlockLoading}
      />
    );
  }

  if (signed_up && noRewards) {
    return (
      <Typography pl={40}>
        You have not earned enough points to qualify for{' '}
        {maxRedemptionCountExceeded ? 'additional' : 'any'} rewards at{' '}
        {storeName}.
      </Typography>
    );
  }

  if (signed_up && !loyaltyPointsStacking && !acknowledgedStacking) {
    return (
      <Box pl={40}>
        <Typography pb={24}>
          This store doesn’t allow loyalty points to be redeemed when there are
          existing discounts applied to your bag.
        </Typography>
        <Button
          full
          onClick={() => setAcknowledgedStacking(true)}
          variant="tertiary"
          label="Use rewards instead"
        />
      </Box>
    );
  }

  return (
    <Box pl={40}>
      {!automatic_redemption && (
        <Banner
          icon={<InfoIcon />}
          label={`Please present your reward account at the time of ${reservationMode}`}
          full
        />
      )}
      {redeemableOnline.length > 0 && (
        <div>
          <Typography mt={16} variant="body-semibold">
            Redeemable online
          </Typography>
          <Box mt={8}>
            <ul>
              {redeemableOnline.map((reward: CrmReward) => {
                return (
                  <ApplyableDiscount
                    key={reward.crm_reward_id}
                    applied={false}
                    dataTestId="applyable-reward"
                    enabled
                    onClick={() => handleApplyDiscount(reward)}
                    text={`${reward.reward_name} `}
                  >
                    <Typography as="span">
                      Use{' '}
                      {pluralize({ noun: 'pt', number: reward.point_value })}
                    </Typography>
                  </ApplyableDiscount>
                );
              })}
            </ul>
          </Box>
        </div>
      )}

      {redeemableInStore.length > 0 && (
        <Box mt={16}>
          <Typography variant="body-semibold">Redeemable in-store</Typography>
          <Flex mt={8}>
            <ul>
              {redeemableInStore.map((reward: CrmReward) => (
                <StoreRedeemableListItem key={reward.crm_reward_id}>
                  <Typography>
                    {reward.reward_name} (
                    {pluralize({ noun: 'pt', number: reward.point_value })})
                  </Typography>
                </StoreRedeemableListItem>
              ))}
            </ul>
          </Flex>
        </Box>
      )}
    </Box>
  );
};
